import React, { useState } from 'react';
import { Camera, Plus, Trash2 } from 'lucide-react';
import { useConstruction } from '../../context/ConstructionContext';
import { toIsoDate } from '../../construction/evm';
import DataTable from '../DataTable';
import Button from '../common/Button';
import ConstructionShell, { FormError, Panel, inputClass, labelClass, runAction, textareaClass } from './ConstructionShell';
import { t } from '../../i18n';

const WEATHER = ['Despejado', 'Nublado', 'Lluvia', 'Viento fuerte', 'Calor extremo', 'Helada'];
const MAX_PHOTOS = 3;

// Reduce la foto a 1024 px (JPEG) para no llenar el almacenamiento del navegador.
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No fue posible leer el archivo adjunto.'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('No fue posible leer el archivo adjunto.'));
      image.onload = () => {
        const scale = Math.min(1, 1024 / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        resolve({ id: `att-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`, name: file.name, type: 'image/jpeg', size: Math.round((dataUrl.length * 3) / 4), dataUrl });
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const blankForm = () => ({
  date: toIsoDate(new Date()),
  weather: 'Despejado',
  crews: [{ name: '', workers: '', hours: '' }],
  activities: '',
  progressNotes: '',
  equipment: '',
  incidents: '',
  attachments: []
});

const crewHours = (crews = []) => crews.reduce((sum, crew) => sum + (Number(crew.workers) || 0) * (Number(crew.hours) || 0), 0);

const DailyLogContent = ({ summary }) => {
  const { state, can, addDailyLog, userName } = useConstruction();
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const logs = state.dailyLogs.filter((log) => log.parkId === summary.parkId).sort((a, b) => (b.date + b.createdAt).localeCompare(a.date + a.createdAt));
  const selected = logs.find((log) => log.id === selectedId) || null;

  const setCrew = (index, key, value) =>
    setForm((prev) => ({ ...prev, crews: prev.crews.map((crew, i) => (i === index ? { ...crew, [key]: value } : crew)) }));

  const addPhotos = async (fileList) => {
    try {
      const files = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
      const room = MAX_PHOTOS - form.attachments.length;
      const photos = await Promise.all(files.slice(0, room).map(compressImage));
      setForm((prev) => ({ ...prev, attachments: [...prev.attachments, ...photos] }));
    } catch (photoError) {
      setError(photoError.message);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    const crews = form.crews
      .filter((crew) => String(crew.name).trim())
      .map((crew) => ({ name: crew.name.trim(), workers: Number(crew.workers) || 0, hours: Number(crew.hours) || 0 }));
    const saved = runAction(() => addDailyLog({ ...form, crews, parkId: summary.parkId }), setError);
    if (saved) {
      setForm(null);
      setSelectedId(saved.id);
    }
  };

  const rows = logs.map((log) => ({
    ...log,
    hh: crewHours(log.crews),
    workers: (log.crews || []).reduce((sum, crew) => sum + (Number(crew.workers) || 0), 0),
    crewSummary: (log.crews || []).map((crew) => `${crew.name} (${crew.workers})`).join('; '),
    author: userName(log.userId),
    photos: (log.attachments || []).length
  }));

  return (
    <div className="space-y-4">
      <Panel
        title="Libro de obra"
        description="Un parte por jornada: clima, dotación (HH), actividades, equipos e incidencias."
        actions={can('daily.edit') && !form ? <Button variant="primary" size="sm" icon={Plus} onClick={() => { setError(''); setForm(blankForm()); }}>{t('Nuevo parte diario')}</Button> : null}
      >
        {form && (
          <form onSubmit={submit} className="mb-4 border border-neutral-200 rounded p-3 bg-neutral-50 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className={labelClass}>{t('Fecha')}</label>
                <input type="date" className={inputClass} value={form.date} onChange={(event) => setForm((prev) => ({ ...prev, date: event.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>{t('Clima')}</label>
                <select className={inputClass} value={form.weather} onChange={(event) => setForm((prev) => ({ ...prev, weather: event.target.value }))}>
                  {WEATHER.map((option) => <option key={option} value={option}>{t(option)}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>{t('Equipos en terreno')}</label>
                <input className={inputClass} value={form.equipment} onChange={(event) => setForm((prev) => ({ ...prev, equipment: event.target.value }))} />
              </div>
            </div>

            <div>
              <label className={labelClass}>{t('Dotación por cuadrilla / contratista')}</label>
              <div className="space-y-2">
                {form.crews.map((crew, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2">
                    <input className={`${inputClass} col-span-6`} placeholder={t('Cuadrilla o contratista')} value={crew.name} onChange={(event) => setCrew(index, 'name', event.target.value)} />
                    <input type="number" min="0" className={`${inputClass} col-span-2`} placeholder={t('Personas')} value={crew.workers} onChange={(event) => setCrew(index, 'workers', event.target.value)} />
                    <input type="number" min="0" step="0.5" className={`${inputClass} col-span-2`} placeholder={t('Horas')} value={crew.hours} onChange={(event) => setCrew(index, 'hours', event.target.value)} />
                    <button type="button" className="col-span-2 text-neutral-500 hover:text-danger-600 flex items-center justify-center" onClick={() => setForm((prev) => ({ ...prev, crews: prev.crews.filter((_, i) => i !== index) }))} title={t('Quitar')}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2">
                <Button size="sm" icon={Plus} onClick={() => setForm((prev) => ({ ...prev, crews: [...prev.crews, { name: '', workers: '', hours: '' }] }))}>{t('Agregar cuadrilla')}</Button>
                <span className="text-xs text-neutral-600">{t('Total HH del día: {0}', crewHours(form.crews))}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>{t('Actividades del día')}</label>
                <textarea rows={3} className={textareaClass} value={form.activities} onChange={(event) => setForm((prev) => ({ ...prev, activities: event.target.value }))} required />
              </div>
              <div>
                <label className={labelClass}>{t('Avance del día')}</label>
                <textarea rows={3} className={textareaClass} value={form.progressNotes} onChange={(event) => setForm((prev) => ({ ...prev, progressNotes: event.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>{t('Incidencias / observaciones')}</label>
                <textarea rows={2} className={textareaClass} value={form.incidents} onChange={(event) => setForm((prev) => ({ ...prev, incidents: event.target.value }))} />
              </div>
            </div>

            <div>
              <label className={labelClass}>{t('Fotos (máx. {0})', MAX_PHOTOS)}</label>
              <div className="flex flex-wrap items-center gap-2">
                {form.attachments.map((photo) => (
                  <div key={photo.id} className="relative">
                    <img src={photo.dataUrl} alt={photo.name} className="w-20 h-20 object-cover rounded border border-neutral-200" />
                    <button type="button" className="absolute -top-1.5 -right-1.5 bg-white border border-neutral-300 rounded-full p-0.5" onClick={() => setForm((prev) => ({ ...prev, attachments: prev.attachments.filter((item) => item.id !== photo.id) }))} title={t('Quitar')}>
                      <Trash2 className="w-3 h-3 text-danger-600" />
                    </button>
                  </div>
                ))}
                {form.attachments.length < MAX_PHOTOS && (
                  <label className="w-20 h-20 border border-dashed border-neutral-300 rounded flex flex-col items-center justify-center text-[11px] text-neutral-500 cursor-pointer hover:bg-white">
                    <Camera className="w-4 h-4 mb-1" />
                    {t('Agregar')}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(event) => addPhotos(event.target.files)} />
                  </label>
                )}
              </div>
            </div>

            <FormError message={error} />
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm">{t('Guardar parte diario')}</Button>
              <Button size="sm" onClick={() => setForm(null)}>{t('Cancelar')}</Button>
            </div>
          </form>
        )}

        <DataTable
          data={rows}
          columns={[
            { key: 'date', label: 'Fecha', sortable: true },
            { key: 'weather', label: 'Clima', render: (value) => t(value) },
            { key: 'workers', label: 'Dotación', align: 'right', sortable: true },
            { key: 'hh', label: 'HH', align: 'right', sortable: true },
            { key: 'crewSummary', label: 'Cuadrillas' },
            { key: 'activities', label: 'Actividades' },
            { key: 'incidents', label: 'Incidencias', render: (value) => value || '—' },
            { key: 'author', label: 'Técnico' },
            { key: 'photos', label: 'Fotos', align: 'right' }
          ]}
          onRowClick={(row) => setSelectedId(row.id)}
          selectedRowId={selectedId}
          searchPlaceholder="Buscar en el libro de obra..."
          emptyTitle="Sin partes diarios"
          emptyDescription="El Técnico de Terreno registra un parte por jornada."
        />
      </Panel>

      {selected && (
        <Panel title={t('Parte diario del {0}', selected.date)} description={t('Registrado por {0} · {1}', userName(selected.userId), selected.createdAt || '')}>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Clima" value={t(selected.weather)} />
            <Detail label="Equipos en terreno" value={selected.equipment} />
            <Detail label="Actividades del día" value={selected.activities} />
            <Detail label="Avance del día" value={selected.progressNotes} />
            <Detail label="Incidencias / observaciones" value={selected.incidents} />
            <div>
              <dt className="text-xs text-neutral-500">{t('Dotación')}</dt>
              <dd className="mt-0.5 text-neutral-800">
                {(selected.crews || []).map((crew) => (
                  <p key={crew.name}>{t('{0}: {1} personas × {2} h', crew.name, crew.workers, crew.hours)}</p>
                ))}
                <p className="font-medium mt-1">{t('Total HH del día: {0}', crewHours(selected.crews))}</p>
              </dd>
            </div>
          </dl>
          {(selected.attachments || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {selected.attachments.map((photo) => (
                <a key={photo.id} href={photo.dataUrl} target="_blank" rel="noreferrer">
                  <img src={photo.dataUrl} alt={photo.name} className="w-32 h-32 object-cover rounded border border-neutral-200" />
                </a>
              ))}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <dt className="text-xs text-neutral-500">{t(label)}</dt>
    <dd className="mt-0.5 text-neutral-800 whitespace-pre-line">{value || '—'}</dd>
  </div>
);

const ConstructionDailyLog = () => (
  <ConstructionShell title="Parte diario / Libro de obra" description="Reporte diario del Técnico de Terreno: clima, dotación, actividades, equipos, incidencias y fotos.">
    {(summary) => <DailyLogContent summary={summary} />}
  </ConstructionShell>
);

export default ConstructionDailyLog;
